import { useCallback, useEffect, useMemo, useState } from "react";
import { CompanyManagementApi } from "../api/company_management_api";
import {
  CompanyManagementState,
  CompanySubscription,
  emptyCompanySummary,
} from "../model/company_management_model";

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export const getCompanyMeta = (subscription: CompanySubscription) => {
  if (Array.isArray(subscription.company)) {
    return subscription.company[0] || null;
  }

  return subscription.company || null;
};

export const getCompanyUserCount = (subscription: CompanySubscription) => {
  const companyMeta = getCompanyMeta(subscription);
  return companyMeta?.total_users ?? subscription.total_users ?? 0;
};

export const formatLabel = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  return value
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const getSubscriptionNote = (subscription: CompanySubscription) => {
  if (subscription.cancel_at_period_end) {
    return "Cancellation scheduled";
  }

  if (subscription.scheduled_tier_name) {
    const scheduledPeriod = subscription.scheduled_billing_period
      ? ` / ${formatLabel(subscription.scheduled_billing_period)}`
      : "";
    return `Change to ${subscription.scheduled_tier_name}${scheduledPeriod}`;
  }

  return "";
};

export const getTierBadgeClass = (tierName: string) => {
  const normalized = tierName.toLowerCase();

  if (normalized.includes("enterprise")) {
    return "bg-indigo-100 text-indigo-800";
  }

  if (normalized.includes("pro")) {
    return "bg-green-100 text-green-800";
  }

  if (normalized.includes("starter")) {
    return "bg-blue-100 text-blue-800";
  }

  return "bg-gray-100 text-gray-800";
};

export const getSubscriptionStatusClass = (status: string) => {
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      return "bg-green-100 text-green-800";
    case "TRIAL":
      return "bg-blue-100 text-blue-800";
    case "PAST_DUE":
      return "bg-yellow-100 text-yellow-800";
    case "CANCELLED":
      return "bg-gray-100 text-gray-700";
    case "EXPIRED":
    case "SUSPENDED":
      return "bg-red-100 text-red-800";
    default:
      return "bg-gray-100 text-gray-700";
  }
};

export const getCompanyStatusClass = (isActive?: boolean | null) => {
  if (isActive === true) {
    return "bg-green-100 text-green-800";
  }

  if (isActive === false) {
    return "bg-red-100 text-red-800";
  }

  return "bg-gray-100 text-gray-700";
};

export const formatCurrency = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  const numericValue = typeof value === "number" ? value : Number(value);

  if (Number.isNaN(numericValue)) {
    return String(value);
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(numericValue);
};

export const formatDate = (value?: string | null) => {
  if (!value) {
    return "-";
  }

  const parsedDate = new Date(value);

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleDateString();
};

export const formatNumber = (value?: number | null) =>
  value === null || value === undefined ? "-" : value.toLocaleString();

export const formatBoolean = (value?: boolean | null) => {
  if (value === null || value === undefined) {
    return "-";
  }

  return value ? "Yes" : "No";
};

export const formatPercent = (value?: string | number | null) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return `${value}%`;
};

export const useCompanyManagementViewModel = () => {
  const [subscriptions, setSubscriptions] = useState<CompanySubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedTier, setSelectedTier] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(emptyCompanySummary);
  const [selectedCompany, setSelectedCompany] =
    useState<CompanySubscription | null>(null);
  const [statusChangeCompany, setStatusChangeCompany] =
    useState<CompanySubscription | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await CompanyManagementApi.getCompanies({
        limit: pageSize,
        offset: (page - 1) * pageSize,
        ...(appliedSearchTerm.trim()
          ? { search: appliedSearchTerm.trim() }
          : {}),
        ...(selectedStatus ? { sub_status: selectedStatus } : {}),
        ...(selectedTier ? { tier_filter: selectedTier } : {}),
      });

      setSubscriptions(data.subscriptions);
      setTotal(data.total);
      setSummary({
        total_active: data.total_active,
        total_trial: data.total_trial,
        total_cancelled: data.total_cancelled,
        total_past_due: data.total_past_due,
        total_expired: data.total_expired,
      });
    } catch (requestError) {
      console.error("Failed to load companies", requestError);
      setError(getErrorMessage(requestError, "Failed to load companies"));
      setSubscriptions([]);
      setTotal(0);
      setSummary(emptyCompanySummary);
    } finally {
      setLoading(false);
    }
  }, [appliedSearchTerm, page, pageSize, selectedStatus, selectedTier]);

  useEffect(() => {
    void loadCompanies();
  }, [loadCompanies]);

  const applyFilters = useCallback(() => {
    setPage(1);
    setAppliedSearchTerm(searchTerm);
  }, [searchTerm]);

  const changeStatusFilter = useCallback((value: string) => {
    setSelectedStatus(value);
    setPage(1);
  }, []);

  const changeTierFilter = useCallback((value: string) => {
    setSelectedTier(value);
    setPage(1);
  }, []);

  const changePageSize = useCallback((nextRowsPerPage: number) => {
    setPage(1);
    setPageSize(nextRowsPerPage);
  }, []);

  const openDetails = useCallback((subscription: CompanySubscription) => {
    setSelectedCompany(subscription);
    setShowDetails(true);
  }, []);

  const closeDetails = useCallback(() => {
    setShowDetails(false);
  }, []);

  const requestCompanyStatusChange = useCallback(
    (company: CompanySubscription) => {
      setError(null);
      setStatusMessage(null);
      setStatusChangeCompany(company);
    },
    [],
  );

  const cancelCompanyStatusChange = useCallback(() => {
    setStatusChangeCompany(null);
  }, []);

  const confirmCompanyStatusChange = useCallback(async () => {
    if (!statusChangeCompany) {
      return;
    }

    const companyMeta = getCompanyMeta(statusChangeCompany);
    const nextActive = companyMeta?.is_active === false;
    const actionLabel = nextActive ? "activate" : "deactivate";

    try {
      setSubmitting(true);
      setError(null);
      setStatusMessage(null);
      setStatusChangeCompany(null);

      const response = await CompanyManagementApi.updateCompanyStatus(
        statusChangeCompany.company_id,
        nextActive,
      );

      if (selectedCompany?.company_id === statusChangeCompany.company_id) {
        setSelectedCompany(null);
        setShowDetails(false);
      }

      await loadCompanies();
      setStatusMessage(
        response.detail || `Company ${nextActive ? "activated" : "deactivated"}.`,
      );
    } catch (requestError) {
      console.error(`Failed to ${actionLabel} company`, requestError);
      setError(
        getErrorMessage(
          requestError,
          `Unable to ${actionLabel} company right now.`,
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }, [loadCompanies, selectedCompany, statusChangeCompany]);

  const state: CompanyManagementState = useMemo(
    () => ({
      subscriptions,
      loading,
      error,
      searchTerm,
      selectedStatus,
      selectedTier,
      page,
      pageSize,
      total,
      summary,
      selectedCompany,
      statusChangeCompany,
      statusMessage,
      showDetails,
      submitting,
    }),
    [
      subscriptions,
      loading,
      error,
      searchTerm,
      selectedStatus,
      selectedTier,
      page,
      pageSize,
      total,
      summary,
      selectedCompany,
      statusChangeCompany,
      statusMessage,
      showDetails,
      submitting,
    ],
  );

  return {
    state,
    setSearchTerm,
    applyFilters,
    changeStatusFilter,
    changeTierFilter,
    setPage,
    changePageSize,
    openDetails,
    closeDetails,
    requestCompanyStatusChange,
    cancelCompanyStatusChange,
    confirmCompanyStatusChange,
    reloadCompanies: loadCompanies,
  };
};
